/**
 * Pattern discovery curation surface.
 *
 * Cross-customer triage page operators use to drain proposed patterns
 * (auto-discovered recurring revenue streams) into one of four
 * statuses — confirmed, rejected, paused, or renamed-and-reconfirmed.
 *
 * The page is journey-centred: the list is the anchor, a right-side
 * Sheet drawer holds detail (sparklines, occurrences, linked forecast
 * bucket rule, action buttons). The drawer's open state is mirrored
 * to ``?pattern=<id>`` so detail views are shareable.
 *
 * Backend boundaries:
 *  - List + counts: views_module composes pattern + customer name +
 *    latest snapshot fields. Server-side filter / sort / q / page.
 *  - Detail + curation actions: pattern_discovery_module.
 *  - Latest run: pattern_discovery_module.
 *
 * See docs/features/pattern-discovery/05_pattern_discovery_module_spec.md.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { PatternListOptions } from "@shared-core/data/adapter";
import type {
  DiscoveryRunSummary,
  PatternCurationItem,
  PatternDetail,
  PatternSnapshot,
  PatternSnapshotResponse,
  PatternStatus,
  PatternStatusCounts,
  PatternTier,
} from "@shared-core/data/api-types";
import type { BulkCurateFilter } from "@shared-core/data/api/pattern-discovery";

import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { Card } from "../app/components/primitives/card";
import { Badge } from "../app/components/primitives/badge";
import { Checkbox } from "../app/components/primitives/checkbox";
import { Skeleton } from "../app/components/primitives/skeleton";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../app/components/primitives/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { currentTime } from "@shared-core/domain/current-time";

// ---------------------------------------------------------------------
// Constants & display helpers
// ---------------------------------------------------------------------

const PAGE_SIZE = 25;

const STATUS_LABEL: Record<PatternStatus, string> = {
  proposed: "Proposed",
  confirmed: "Confirmed",
  rejected: "Rejected",
  paused: "Paused",
};

const TIER_LABEL: Record<PatternTier, string> = {
  green: "Green",
  amber: "Amber",
  red: "Red",
};

const TIER_DOT_CLASS: Record<PatternTier, string> = {
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-rose-500",
};

const STATUS_BADGE_CLASS: Record<PatternStatus, string> = {
  proposed: "bg-blue-100 text-blue-900",
  confirmed: "bg-emerald-100 text-emerald-900",
  rejected: "bg-rose-100 text-rose-900",
  paused: "bg-slate-200 text-slate-800",
};

type SnapshotLike =
  PatternSnapshot | PatternSnapshotResponse | null | undefined;

function formatCadence(snap: SnapshotLike): string {
  if (!snap || !snap.cadence_days_median) return "—";
  return `~${Math.round(snap.cadence_days_median)}d`;
}

function formatLastSeen(snap: SnapshotLike): string {
  if (!snap) return "—";
  const days = snap.days_since_last_occurrence;
  if (days === 0) return "today";
  if (days === 1) return "1d ago";
  return `${days}d ago`;
}

function formatNextDue(snap: SnapshotLike): string {
  if (!snap?.next_expected_date_offset_days) return "—";
  return `in ${snap.next_expected_date_offset_days}d`;
}

function formatGBP(amount: number | null | undefined): string {
  if (amount == null) return "—";
  return `£${amount.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "never";
  const ms = currentTime().getTime() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------

export function PatternDiscoveryPage() {
  const adapter = useDataAdapter();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Filter / sort / page state ─────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<PatternStatus | "all">(
    "proposed",
  );
  const [tierFilter, setTierFilter] = useState<PatternTier | "all">("all");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);

  // Rich metric filters
  const [medianAmountMin, setMedianAmountMin] = useState<string>("");
  const [medianAmountMax, setMedianAmountMax] = useState<string>("");
  const [nOccL12mMin, setNOccL12mMin] = useState<string>("");
  const [nOccL12mMax, setNOccL12mMax] = useState<string>("");
  const [cadenceMin, setCadenceMin] = useState<string>("");
  const [cadenceMax, setCadenceMax] = useState<string>("");
  const [nextExpectedDays, setNextExpectedDays] = useState<string>("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Sort state
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // ── Server data ────────────────────────────────────────────────────
  const [items, setItems] = useState<PatternCurationItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<PatternStatusCounts | null>(null);
  const [latestRun, setLatestRun] = useState<DiscoveryRunSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ── Selection ─────────────────────────────────────────────────────
  // "page" = individual IDs on page; "filter" = the entire matching set
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState<"page" | "filter">("page");

  // ── Drawer / dialog state ──────────────────────────────────────────
  const drawerPatternId = searchParams.get("pattern");
  const [rejectDialog, setRejectDialog] = useState<
    | { kind: "single"; item: PatternCurationItem }
    | { kind: "bulk"; items: PatternCurationItem[] }
    | null
  >(null);
  const [pauseDialog, setPauseDialog] = useState<PatternCurationItem | null>(
    null,
  );

  // ── Data fetch ─────────────────────────────────────────────────────
  const buildOptions = useCallback((): PatternListOptions => {
    const opts: PatternListOptions = {
      page,
      pageSize: PAGE_SIZE,
    };
    if (statusFilter !== "all") opts.status = [statusFilter];
    if (tierFilter !== "all") opts.tier = [tierFilter];
    if (searchText.trim()) opts.q = searchText.trim();
    // Metric filters
    const amtMin = parseFloat(medianAmountMin);
    if (!isNaN(amtMin)) opts.medianAmountMin = amtMin;
    const amtMax = parseFloat(medianAmountMax);
    if (!isNaN(amtMax)) opts.medianAmountMax = amtMax;
    const occMin = parseInt(nOccL12mMin);
    if (!isNaN(occMin)) opts.nOccurrencesL12mMin = occMin;
    const occMax = parseInt(nOccL12mMax);
    if (!isNaN(occMax)) opts.nOccurrencesL12mMax = occMax;
    const cadMin = parseInt(cadenceMin);
    if (!isNaN(cadMin)) opts.cadenceDaysMedianMin = cadMin;
    const cadMax = parseInt(cadenceMax);
    if (!isNaN(cadMax)) opts.cadenceDaysMedianMax = cadMax;
    const nextDays = parseInt(nextExpectedDays);
    if (!isNaN(nextDays)) opts.nextExpectedWithinDays = nextDays;
    // Sort
    if (sortField) opts.sort = `${sortDir === "desc" ? "-" : ""}${sortField}`;
    return opts;
  }, [
    page,
    statusFilter,
    tierFilter,
    searchText,
    medianAmountMin,
    medianAmountMax,
    nOccL12mMin,
    nOccL12mMax,
    cadenceMin,
    cadenceMax,
    nextExpectedDays,
    sortField,
    sortDir,
  ]);

  /** Build the BulkCurateFilter from current UI filter state. */
  const buildBulkFilter = useCallback((): BulkCurateFilter => {
    const f: BulkCurateFilter = {};
    if (statusFilter !== "all") f.status = [statusFilter];
    if (tierFilter !== "all") f.tier = [tierFilter];
    if (searchText.trim()) f.q = searchText.trim();
    const amtMin = parseFloat(medianAmountMin);
    if (!isNaN(amtMin)) f.median_amount_min = amtMin;
    const amtMax = parseFloat(medianAmountMax);
    if (!isNaN(amtMax)) f.median_amount_max = amtMax;
    const occMin = parseInt(nOccL12mMin);
    if (!isNaN(occMin)) f.n_occurrences_l12m_min = occMin;
    const occMax = parseInt(nOccL12mMax);
    if (!isNaN(occMax)) f.n_occurrences_l12m_max = occMax;
    const cadMin = parseInt(cadenceMin);
    if (!isNaN(cadMin)) f.cadence_days_median_min = cadMin;
    const cadMax = parseInt(cadenceMax);
    if (!isNaN(cadMax)) f.cadence_days_median_max = cadMax;
    const nextDays = parseInt(nextExpectedDays);
    if (!isNaN(nextDays)) f.next_expected_within_days = nextDays;
    return f;
  }, [
    statusFilter,
    tierFilter,
    searchText,
    medianAmountMin,
    medianAmountMax,
    nOccL12mMin,
    nOccL12mMax,
    cadenceMin,
    cadenceMax,
    nextExpectedDays,
  ]);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const [list, statusCounts, run] = await Promise.all([
        adapter.listPatternCuration(buildOptions()),
        adapter.getPatternStatusCounts({
          tier: tierFilter !== "all" ? [tierFilter] : undefined,
          q: searchText.trim() || undefined,
        }),
        adapter.getLatestDiscoveryRun(),
      ]);
      setItems(list.items);
      setTotal(list.total);
      setCounts(statusCounts);
      setLatestRun(run);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load patterns");
      setItems([]);
    }
  }, [adapter, buildOptions, tierFilter, searchText]);

  // Reset page + selection when any filter changes; refetch.
  const filterKey = `${statusFilter}|${tierFilter}|${searchText}|${medianAmountMin}|${medianAmountMax}|${nOccL12mMin}|${nOccL12mMax}|${cadenceMin}|${cadenceMax}|${nextExpectedDays}`;
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
    setSelectionMode("page");
  }, [filterKey]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // ── Selection helpers ──────────────────────────────────────────────
  const visibleIds = useMemo(() => (items ?? []).map((p) => p.id), [items]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const selectedItems = useMemo(
    () => (items ?? []).filter((p) => selectedIds.has(p.id)),
    [items, selectedIds],
  );
  const proposedSelected = selectedItems.filter((p) => p.status === "proposed");

  function toggleAllVisible() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  }

  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ── Drawer open / close ────────────────────────────────────────────
  function openDrawer(patternId: string) {
    const next = new URLSearchParams(searchParams);
    next.set("pattern", patternId);
    setSearchParams(next, { replace: true });
  }
  function closeDrawer() {
    const next = new URLSearchParams(searchParams);
    next.delete("pattern");
    setSearchParams(next, { replace: true });
  }

  // ── Sort helper ─────────────────────────────────────────────────────
  function toggleSort(field: string) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  // ── Curation actions ───────────────────────────────────────────────
  async function handleConfirm(item: PatternCurationItem) {
    try {
      await adapter.confirmPattern(item.id);
      toast.success(`Confirmed ${item.name}`);
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Confirm failed");
    }
  }

  async function handleBulkAction(
    action: "confirm" | "reject" | "pause",
    reason = "",
  ) {
    if (selectionMode === "filter") {
      // Server-side bulk curate via filter.
      try {
        const result = await adapter.bulkCuratePatterns({
          filter: buildBulkFilter(),
          action,
          reason,
        });
        const msg = `${action === "confirm" ? "Confirmed" : action === "reject" ? "Rejected" : "Paused"} ${result.succeeded} pattern${result.succeeded === 1 ? "" : "s"}`;
        if (result.failed > 0) {
          toast.warning(`${msg}, ${result.failed} failed`);
        } else {
          toast.success(msg);
        }
        setSelectedIds(new Set());
        setSelectionMode("page");
        await reload();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : `Bulk ${action} failed`);
      }
    } else {
      // Per-row action for page selection.
      const targets = [...selectedIds];
      try {
        if (action === "confirm") {
          await Promise.all(targets.map((id) => adapter.confirmPattern(id)));
        } else if (action === "reject") {
          await Promise.all(
            targets.map((id) => adapter.rejectPattern(id, { reason })),
          );
        } else {
          await Promise.all(
            targets.map((id) => adapter.pausePattern(id, { reason })),
          );
        }
        toast.success(
          `${action === "confirm" ? "Confirmed" : action === "reject" ? "Rejected" : "Paused"} ${targets.length} pattern${targets.length === 1 ? "" : "s"}`,
        );
        setSelectedIds(new Set());
        setSelectionMode("page");
        await reload();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : `Bulk ${action} failed`);
      }
    }
  }

  async function handleReject(reason: string) {
    if (!rejectDialog) return;
    if (rejectDialog.kind === "bulk") {
      await handleBulkAction("reject", reason);
      setRejectDialog(null);
      return;
    }
    try {
      await adapter.rejectPattern(rejectDialog.item.id, { reason });
      toast.success(`Rejected ${rejectDialog.item.name}`);
      setRejectDialog(null);
      setSelectedIds(new Set());
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Reject failed");
    }
  }

  async function handlePause(reason: string) {
    if (!pauseDialog) return;
    try {
      await adapter.pausePattern(pauseDialog.id, { reason });
      toast.success(`Paused ${pauseDialog.name}`);
      setPauseDialog(null);
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Pause failed");
    }
  }

  // ── Render ─────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="container mx-auto p-6 max-w-7xl space-y-4">
      <PageHeader latestRun={latestRun} />
      <CountsStrip counts={counts} />

      <Card className="p-5 space-y-4">
        <FilterPanel
          status={statusFilter}
          onStatus={setStatusFilter}
          tier={tierFilter}
          onTier={setTierFilter}
          search={searchText}
          onSearch={setSearchText}
          medianAmountMin={medianAmountMin}
          onMedianAmountMin={setMedianAmountMin}
          medianAmountMax={medianAmountMax}
          onMedianAmountMax={setMedianAmountMax}
          nOccL12mMin={nOccL12mMin}
          onNOccL12mMin={setNOccL12mMin}
          nOccL12mMax={nOccL12mMax}
          onNOccL12mMax={setNOccL12mMax}
          cadenceMin={cadenceMin}
          onCadenceMin={setCadenceMin}
          cadenceMax={cadenceMax}
          onCadenceMax={setCadenceMax}
          nextExpectedDays={nextExpectedDays}
          onNextExpectedDays={setNextExpectedDays}
          showAdvanced={showAdvancedFilters}
          onToggleAdvanced={() => setShowAdvancedFilters((v) => !v)}
        />

        <BulkToolbar
          selectedCount={selectionMode === "filter" ? total : selectedIds.size}
          selectionMode={selectionMode}
          total={total}
          proposedSelected={proposedSelected}
          onConfirm={() => void handleBulkAction("confirm")}
          onReject={() =>
            setRejectDialog({ kind: "bulk", items: selectedItems })
          }
          onPause={() => void handleBulkAction("pause")}
          onClear={() => {
            setSelectedIds(new Set());
            setSelectionMode("page");
          }}
          onSelectAllMatching={() => setSelectionMode("filter")}
        />

        {error && <ErrorBox message={error} onRetry={() => void reload()} />}

        {items === null ? (
          <SkeletonRows />
        ) : items.length === 0 ? (
          <EmptyState statusFilter={statusFilter} />
        ) : (
          <>
            <PatternsTable
              items={items}
              selectedIds={selectedIds}
              allVisibleSelected={allVisibleSelected}
              onToggleAll={toggleAllVisible}
              onToggleOne={toggleOne}
              onOpenDrawer={openDrawer}
              sortField={sortField}
              sortDir={sortDir}
              onSort={toggleSort}
            />
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              onPage={setPage}
            />
          </>
        )}
      </Card>

      <PatternDetailDrawer
        patternId={drawerPatternId}
        item={
          drawerPatternId
            ? (items?.find((p) => p.id === drawerPatternId) ?? null)
            : null
        }
        onClose={closeDrawer}
        onConfirm={async (id) => {
          const item = items?.find((p) => p.id === id);
          if (item) await handleConfirm(item);
        }}
        onReject={(item) => setRejectDialog({ kind: "single", item })}
        onPause={(item) => setPauseDialog(item)}
        onRenamed={() => void reload()}
      />

      {rejectDialog && (
        <RejectDialog
          dialog={rejectDialog}
          onCancel={() => setRejectDialog(null)}
          onSubmit={handleReject}
        />
      )}
      {pauseDialog && (
        <PauseDialog
          item={pauseDialog}
          onCancel={() => setPauseDialog(null)}
          onSubmit={handlePause}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------

function PageHeader({ latestRun }: { latestRun: DiscoveryRunSummary | null }) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold">Pattern discovery</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Review newly-discovered recurring patterns. Confirm to create a
          forecast bucket rule. Reject to suppress for future runs. Pause to
          temporarily stop forecasting without rejecting.
        </p>
      </div>
      <div className="text-xs text-muted-foreground shrink-0">
        Last discovery run:{" "}
        <span className="font-medium text-foreground">
          {formatRelativeTime(
            latestRun?.finished_at ?? latestRun?.started_at ?? null,
          )}
        </span>
        {latestRun && (
          <span className="ml-2">
            · {latestRun.patterns_upserted} patterns ·{" "}
            {latestRun.patterns_promoted_to_cohort_rule} promoted
          </span>
        )}
      </div>
    </div>
  );
}

function CountsStrip({ counts }: { counts: PatternStatusCounts | null }) {
  if (!counts) {
    return (
      <div className="flex gap-4 text-sm">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-5 w-24" />
        ))}
      </div>
    );
  }
  const entries: Array<{ key: PatternStatus; label: string; value: number }> = [
    { key: "proposed", label: "proposed", value: counts.proposed },
    { key: "confirmed", label: "confirmed", value: counts.confirmed },
    { key: "rejected", label: "rejected", value: counts.rejected },
    { key: "paused", label: "paused", value: counts.paused },
  ];
  return (
    <div className="flex gap-4 text-sm text-muted-foreground">
      {entries.map((e, idx) => (
        <span key={e.key}>
          <span className="font-medium text-foreground">{e.value}</span>{" "}
          {e.label}
          {idx < entries.length - 1 && (
            <span className="ml-4 text-muted-foreground/50">·</span>
          )}
        </span>
      ))}
    </div>
  );
}

function FilterPanel({
  status,
  onStatus,
  tier,
  onTier,
  search,
  onSearch,
  medianAmountMin,
  onMedianAmountMin,
  medianAmountMax,
  onMedianAmountMax,
  nOccL12mMin,
  onNOccL12mMin,
  nOccL12mMax,
  onNOccL12mMax,
  cadenceMin,
  onCadenceMin,
  cadenceMax,
  onCadenceMax,
  nextExpectedDays,
  onNextExpectedDays,
  showAdvanced,
  onToggleAdvanced,
}: {
  status: PatternStatus | "all";
  onStatus: (v: PatternStatus | "all") => void;
  tier: PatternTier | "all";
  onTier: (v: PatternTier | "all") => void;
  search: string;
  onSearch: (v: string) => void;
  medianAmountMin: string;
  onMedianAmountMin: (v: string) => void;
  medianAmountMax: string;
  onMedianAmountMax: (v: string) => void;
  nOccL12mMin: string;
  onNOccL12mMin: (v: string) => void;
  nOccL12mMax: string;
  onNOccL12mMax: (v: string) => void;
  cadenceMin: string;
  onCadenceMin: (v: string) => void;
  cadenceMax: string;
  onCadenceMax: (v: string) => void;
  nextExpectedDays: string;
  onNextExpectedDays: (v: string) => void;
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs flex items-center gap-2">
          <span className="text-muted-foreground">Status</span>
          <Select
            value={status}
            onValueChange={(v) => onStatus(v as PatternStatus | "all")}
          >
            <SelectTrigger className="w-[140px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="proposed">Proposed</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className="text-xs flex items-center gap-2">
          <span className="text-muted-foreground">Tier</span>
          <Select
            value={tier}
            onValueChange={(v) => onTier(v as PatternTier | "all")}
          >
            <SelectTrigger className="w-[120px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="green">Green</SelectItem>
              <SelectItem value="amber">Amber</SelectItem>
              <SelectItem value="red">Red</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <Input
          type="search"
          placeholder="Search name or slug…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          className="max-w-sm h-8 text-sm"
        />
        <Button
          size="sm"
          variant="ghost"
          className="text-xs"
          onClick={onToggleAdvanced}
        >
          {showAdvanced ? "Hide filters" : "More filters"}
        </Button>
      </div>

      {showAdvanced && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-xs">
          <fieldset className="space-y-1">
            <legend className="text-muted-foreground">
              Median amount (GBP)
            </legend>
            <div className="flex gap-1">
              <Input
                type="number"
                placeholder="Min"
                value={medianAmountMin}
                onChange={(e) => onMedianAmountMin(e.target.value)}
                className="h-7 text-xs w-full"
                min="0"
              />
              <Input
                type="number"
                placeholder="Max"
                value={medianAmountMax}
                onChange={(e) => onMedianAmountMax(e.target.value)}
                className="h-7 text-xs w-full"
                min="0"
              />
            </div>
          </fieldset>
          <fieldset className="space-y-1">
            <legend className="text-muted-foreground">Occurrences (12m)</legend>
            <div className="flex gap-1">
              <Input
                type="number"
                placeholder="Min"
                value={nOccL12mMin}
                onChange={(e) => onNOccL12mMin(e.target.value)}
                className="h-7 text-xs w-full"
                min="0"
              />
              <Input
                type="number"
                placeholder="Max"
                value={nOccL12mMax}
                onChange={(e) => onNOccL12mMax(e.target.value)}
                className="h-7 text-xs w-full"
                min="0"
              />
            </div>
          </fieldset>
          <fieldset className="space-y-1">
            <legend className="text-muted-foreground">Cadence (days)</legend>
            <div className="flex gap-1">
              <Input
                type="number"
                placeholder="Min"
                value={cadenceMin}
                onChange={(e) => onCadenceMin(e.target.value)}
                className="h-7 text-xs w-full"
                min="0"
              />
              <Input
                type="number"
                placeholder="Max"
                value={cadenceMax}
                onChange={(e) => onCadenceMax(e.target.value)}
                className="h-7 text-xs w-full"
                min="0"
              />
            </div>
          </fieldset>
          <fieldset className="space-y-1">
            <legend className="text-muted-foreground">
              Next expected within
            </legend>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                placeholder="days"
                value={nextExpectedDays}
                onChange={(e) => onNextExpectedDays(e.target.value)}
                className="h-7 text-xs w-full"
                min="0"
              />
              <span className="text-muted-foreground">days</span>
            </div>
          </fieldset>
        </div>
      )}
    </div>
  );
}

function BulkToolbar({
  selectedCount,
  selectionMode,
  total,
  proposedSelected,
  onConfirm,
  onReject,
  onPause,
  onClear,
  onSelectAllMatching,
}: {
  selectedCount: number;
  selectionMode: "page" | "filter";
  total: number;
  proposedSelected: PatternCurationItem[];
  onConfirm: () => void;
  onReject: () => void;
  onPause: () => void;
  onClear: () => void;
  onSelectAllMatching: () => void;
}) {
  if (selectedCount === 0) return null;
  const isFilterMode = selectionMode === "filter";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 rounded border bg-muted/40 px-3 py-2">
        <span className="text-sm font-medium">
          {isFilterMode ? `All ${total} matching` : `${selectedCount} selected`}
        </span>
        {!isFilterMode && total > selectedCount && (
          <button
            className="text-xs text-primary hover:underline"
            onClick={onSelectAllMatching}
          >
            Select all {total} matching filter
          </button>
        )}
        <div className="flex gap-2 ml-2">
          <Button size="sm" onClick={onConfirm}>
            Confirm{" "}
            {isFilterMode
              ? `all ${total}`
              : proposedSelected.length > 0 &&
                  proposedSelected.length !== selectedCount
                ? `${proposedSelected.length} proposed`
                : `${selectedCount}`}
          </Button>
          <Button size="sm" variant="outline" onClick={onReject}>
            Reject {isFilterMode ? `all ${total}` : selectedCount}…
          </Button>
          <Button size="sm" variant="outline" onClick={onPause}>
            Pause {isFilterMode ? `all ${total}` : selectedCount}
          </Button>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClear}
          className="ml-auto text-xs"
        >
          Clear selection
        </Button>
      </div>
    </div>
  );
}

function SortableHead({
  field,
  currentField,
  currentDir,
  onSort,
  className,
  children,
}: {
  field: string;
  currentField: string | null;
  currentDir: "asc" | "desc";
  onSort: (f: string) => void;
  className?: string;
  children: React.ReactNode;
}) {
  const active = currentField === field;
  return (
    <TableHead
      className={`cursor-pointer select-none ${className ?? ""}`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active &&
          (currentDir === "asc" ? (
            <ChevronUpIcon className="size-3" />
          ) : (
            <ChevronDownIcon className="size-3" />
          ))}
      </span>
    </TableHead>
  );
}

function PatternsTable({
  items,
  selectedIds,
  allVisibleSelected,
  onToggleAll,
  onToggleOne,
  onOpenDrawer,
  sortField,
  sortDir,
  onSort,
}: {
  items: PatternCurationItem[];
  selectedIds: Set<string>;
  allVisibleSelected: boolean;
  onToggleAll: () => void;
  onToggleOne: (id: string) => void;
  onOpenDrawer: (id: string) => void;
  sortField: string | null;
  sortDir: "asc" | "desc";
  onSort: (field: string) => void;
}) {
  const sortProps = { currentField: sortField, currentDir: sortDir, onSort };
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8">
            <Checkbox
              checked={allVisibleSelected}
              onCheckedChange={onToggleAll}
              aria-label="Select all visible"
            />
          </TableHead>
          <SortableHead field="tier" className="w-16" {...sortProps}>
            Tier
          </SortableHead>
          <SortableHead field="name" {...sortProps}>
            Pattern
          </SortableHead>
          <SortableHead field="customer_name" {...sortProps}>
            Customer
          </SortableHead>
          <SortableHead
            field="cadence_days_median"
            className="w-20"
            {...sortProps}
          >
            Cadence
          </SortableHead>
          <SortableHead field="last_seen_at" className="w-24" {...sortProps}>
            Last seen
          </SortableHead>
          <SortableHead
            field="next_expected_date_offset_days"
            className="w-20"
            {...sortProps}
          >
            Next due
          </SortableHead>
          <SortableHead
            field="median_amount"
            className="w-28 text-right"
            {...sortProps}
          >
            Median amt
          </SortableHead>
          <SortableHead field="status" className="w-24" {...sortProps}>
            Status
          </SortableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((p) => {
          const checked = selectedIds.has(p.id);
          return (
            <TableRow
              key={p.id}
              data-state={checked ? "selected" : undefined}
              className="cursor-pointer"
              onClick={(e) => {
                // Avoid opening the drawer when clicking the checkbox cell.
                if ((e.target as HTMLElement).closest("[data-no-row-click]"))
                  return;
                onOpenDrawer(p.id);
              }}
            >
              <TableCell data-no-row-click onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => onToggleOne(p.id)}
                  aria-label={`Select ${p.name}`}
                />
              </TableCell>
              <TableCell>
                <span
                  className={`inline-block size-2 rounded-full ${TIER_DOT_CLASS[p.tier]}`}
                  aria-label={`tier ${TIER_LABEL[p.tier]}`}
                  title={TIER_LABEL[p.tier]}
                />
              </TableCell>
              <TableCell className="font-medium">{p.name}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {p.customer_name}
              </TableCell>
              <TableCell className="text-sm font-mono">
                {formatCadence(p.latest_snapshot)}
              </TableCell>
              <TableCell className="text-sm">
                {formatLastSeen(p.latest_snapshot)}
              </TableCell>
              <TableCell className="text-sm">
                {formatNextDue(p.latest_snapshot)}
              </TableCell>
              <TableCell className="text-sm font-mono text-right">
                {formatGBP(p.latest_snapshot?.median_amount)}
              </TableCell>
              <TableCell>
                <Badge
                  className={`text-[10px] ${STATUS_BADGE_CLASS[p.status]}`}
                  variant="secondary"
                >
                  {STATUS_LABEL[p.status]}
                </Badge>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPage: (p: number) => void;
}) {
  if (totalPages <= 1) {
    return (
      <div className="text-xs text-muted-foreground">
        {total} pattern{total === 1 ? "" : "s"}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between text-xs text-muted-foreground">
      <span>
        Showing page <span className="font-medium text-foreground">{page}</span>{" "}
        of <span className="font-medium text-foreground">{totalPages}</span> ·{" "}
        {total} pattern{total === 1 ? "" : "s"}
      </span>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="outline"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function EmptyState({ statusFilter }: { statusFilter: PatternStatus | "all" }) {
  if (statusFilter === "proposed") {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <div className="text-2xl mb-2" aria-hidden>
          ✓
        </div>
        <p className="font-medium text-foreground">Nothing to review</p>
        <p className="text-sm mt-1 max-w-md mx-auto">
          No proposed patterns. The next discovery run will surface any new
          patterns here. Switch the status filter above to see confirmed,
          rejected, or paused patterns.
        </p>
      </div>
    );
  }
  return (
    <div className="text-center py-12 text-muted-foreground">
      <p>No patterns match the current filters.</p>
    </div>
  );
}

function ErrorBox({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex items-center justify-between text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
      <span>⚠ {message}</span>
      <Button size="sm" variant="outline" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------

function PatternDetailDrawer({
  patternId,
  item,
  onClose,
  onConfirm,
  onReject,
  onPause,
  onRenamed,
}: {
  patternId: string | null;
  /** Curation row from the list, used for forecast_bucket_rule_id deep-link.
   *  Null when the drawer was opened via direct URL to a pattern not in
   *  the current list view. */
  item: PatternCurationItem | null;
  onClose: () => void;
  onConfirm: (id: string) => Promise<void>;
  onReject: (item: PatternCurationItem) => void;
  onPause: (item: PatternCurationItem) => void;
  onRenamed: () => void;
}) {
  const adapter = useDataAdapter();
  const [detail, setDetail] = useState<PatternDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    if (!patternId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    adapter
      .getPatternDetail(patternId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setRenameValue(d.pattern.name);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(e instanceof Error ? e.message : "Couldn't load pattern");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [patternId, adapter]);

  const open = patternId !== null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto px-6 sm:px-6 pb-8">
        {loading && !detail ? (
          <div className="space-y-3 mt-6">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : detail ? (
          <PatternDrawerBody
            detail={detail}
            forecastBucketRuleId={item?.forecast_bucket_rule_id ?? null}
            renameValue={renameValue}
            onRenameChange={setRenameValue}
            savingName={savingName}
            onSaveName={async () => {
              if (!renameValue.trim() || renameValue === detail.pattern.name)
                return;
              setSavingName(true);
              try {
                await adapter.renamePattern(detail.pattern.id, {
                  name: renameValue.trim(),
                });
                toast.success("Name updated");
                onRenamed();
                const refreshed = await adapter.getPatternDetail(
                  detail.pattern.id,
                );
                setDetail(refreshed);
              } catch (e) {
                toast.error(e instanceof Error ? e.message : "Rename failed");
              } finally {
                setSavingName(false);
              }
            }}
            onConfirm={() => onConfirm(detail.pattern.id)}
            onReject={() => onReject(detailToCurationItem(detail))}
            onPause={() => onPause(detailToCurationItem(detail))}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function detailToCurationItem(detail: PatternDetail): PatternCurationItem {
  // Detail does not carry the customer name or the bucket-rule link; the
  // parent already has a curation row for items in the current list view
  // and passes ``forecastBucketRuleId`` separately. This shim only exists
  // for the curation modals' API, which need an item-shaped argument.
  return {
    type: "pattern",
    _self: `/pattern-discovery/patterns/${detail.pattern.id}`,
    id: detail.pattern.id,
    customer_id: detail.pattern.customer_id,
    customer_name: "—",
    slug: detail.pattern.slug,
    name: detail.pattern.name,
    tier: detail.pattern.tier as PatternTier,
    status: detail.pattern.status as PatternStatus,
    auto_promotion_eligible: detail.pattern.auto_promotion_eligible,
    latest_snapshot: detail.pattern.latest_snapshot
      ? snapshotToEmbed(detail.pattern.latest_snapshot)
      : null,
    forecast_bucket_rule_id: null,
    updated_at: detail.pattern.updated_at,
  };
}

function snapshotToEmbed(snap: PatternSnapshotResponse): PatternSnapshot {
  return {
    as_of_date: snap.as_of_date,
    n_occurrences_l24m: snap.n_occurrences_l24m,
    n_occurrences_l12m: snap.n_occurrences_l12m,
    median_amount: snap.median_amount,
    cadence_days_median: snap.cadence_days_median,
    tier: snap.tier as PatternTier,
    tier_score: snap.tier_score,
    days_since_last_occurrence: snap.days_since_last_occurrence,
    next_expected_date_offset_days: snap.next_expected_date_offset_days ?? null,
    auto_promotable: snap.auto_promotable,
  };
}

function PatternDrawerBody({
  detail,
  forecastBucketRuleId,
  renameValue,
  onRenameChange,
  savingName,
  onSaveName,
  onConfirm,
  onReject,
  onPause,
}: {
  detail: PatternDetail;
  forecastBucketRuleId: string | null;
  renameValue: string;
  onRenameChange: (v: string) => void;
  savingName: boolean;
  onSaveName: () => void | Promise<void>;
  onConfirm: () => void | Promise<void>;
  onReject: () => void;
  onPause: () => void;
}) {
  const { pattern, occurrences, trend } = detail;
  const tier = pattern.tier as PatternTier;
  const status = pattern.status as PatternStatus;
  const latest = pattern.latest_snapshot;

  return (
    <>
      <SheetHeader className="px-0">
        <div className="flex items-center gap-2">
          <span
            className={`inline-block size-2.5 rounded-full ${TIER_DOT_CLASS[tier]}`}
          />
          <SheetTitle className="text-lg">{pattern.name}</SheetTitle>
          <Badge
            className={`text-[10px] ${STATUS_BADGE_CLASS[status]}`}
            variant="secondary"
          >
            {STATUS_LABEL[status]}
          </Badge>
        </div>
        <SheetDescription className="font-mono text-[11px]">
          {pattern.slug}
        </SheetDescription>
      </SheetHeader>

      {(status === "rejected" || status === "paused") && (
        <RejectionOrPauseBanner
          status={status}
          updatedAt={pattern.updated_at ?? null}
        />
      )}

      {/* Stats */}
      <section className="space-y-3 mt-4">
        <StatRow
          label="Cadence"
          sparkline={
            <Sparkline
              values={trend.map((t) => t.cadence_days_median).reverse()}
              ariaLabel="Cadence trend"
            />
          }
          value={latest ? `~${Math.round(latest.cadence_days_median)}d` : "—"}
        />
        <StatRow
          label="Amount"
          sparkline={
            <Sparkline
              values={trend.map((t) => t.median_amount).reverse()}
              ariaLabel="Amount trend"
            />
          }
          value={formatGBP(latest?.median_amount)}
        />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {latest?.n_occurrences_l12m ?? 0} occurrences in last 12m
            {latest?.auto_promotable && " · auto-promotable"}
          </span>
          {latest?.next_expected_date_offset_days != null && (
            <span>
              Next expected in{" "}
              <span className="text-foreground font-medium">
                {latest.next_expected_date_offset_days}d
              </span>
            </span>
          )}
        </div>
      </section>

      {/* Match expression */}
      <section className="mt-5">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
          Match expression
        </h3>
        <code className="block font-mono text-[11px] bg-muted/40 border rounded px-2 py-1">
          {pattern.match_expression}
        </code>
      </section>

      {/* Linked bucket rule */}
      <section className="mt-5">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
          Forecast bucket rule
        </h3>
        {forecastBucketRuleId ? (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Linked rule (auto-managed by pattern discovery)
            </span>
            {/* No "Open" link: /settings/forecast-categorisation was never
                built — it is in neither the route manifest nor the router — so
                the link 404'd in every environment. The rule id is shown
                instead, which is what a reader actually needs. */}
            <code className="text-xs text-muted-foreground">
              {forecastBucketRuleId}
            </code>
          </div>
        ) : status === "rejected" ? (
          <p className="text-sm text-muted-foreground italic">
            None — rejection prevents auto-create.
          </p>
        ) : status === "paused" ? (
          <p className="text-sm text-muted-foreground italic">None — paused.</p>
        ) : status === "proposed" ? (
          <p className="text-sm text-muted-foreground italic">
            None — will be created on confirm.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground italic">None.</p>
        )}
      </section>

      {/* Occurrences */}
      <section className="mt-5">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
          Recent occurrences
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Due</TableHead>
              <TableHead className="w-20 text-right">Amount</TableHead>
              <TableHead>Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {occurrences.slice(0, 6).map((o) => (
              <TableRow key={o.id}>
                <TableCell className="text-xs font-mono">
                  {o.due_date}
                </TableCell>
                <TableCell className="text-xs font-mono text-right">
                  {formatGBP(o.booking_amt)}
                </TableCell>
                <TableCell className="text-xs">{o.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      {/* Rename */}
      <section className="mt-5">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
          Name
        </h3>
        <div className="flex gap-2">
          <Input
            value={renameValue}
            onChange={(e) => onRenameChange(e.target.value)}
            placeholder="Operator-friendly label"
          />
          <Button
            size="sm"
            variant="outline"
            disabled={
              savingName || !renameValue.trim() || renameValue === pattern.name
            }
            onClick={() => void onSaveName()}
          >
            {savingName ? "Saving…" : "Save"}
          </Button>
        </div>
      </section>

      {/* Actions */}
      <section className="mt-6 flex flex-wrap gap-2">
        <DrawerActions
          status={status}
          onConfirm={onConfirm}
          onReject={onReject}
          onPause={onPause}
        />
      </section>
    </>
  );
}

function DrawerActions({
  status,
  onConfirm,
  onReject,
  onPause,
}: {
  status: PatternStatus;
  onConfirm: () => void | Promise<void>;
  onReject: () => void;
  onPause: () => void;
}) {
  switch (status) {
    case "proposed":
      return (
        <>
          <Button onClick={() => void onConfirm()}>
            Confirm &amp; create rule
          </Button>
          <Button variant="outline" onClick={onReject}>
            Reject…
          </Button>
          <Button variant="outline" onClick={onPause}>
            Pause…
          </Button>
        </>
      );
    case "confirmed":
      return (
        <>
          <Button variant="outline" onClick={onPause}>
            Pause…
          </Button>
          <Button variant="outline" onClick={onReject}>
            Reject…
          </Button>
        </>
      );
    case "rejected":
      return (
        <Button onClick={() => void onConfirm()}>
          Re-confirm &amp; create rule
        </Button>
      );
    case "paused":
      return (
        <>
          <Button onClick={() => void onConfirm()}>
            Re-confirm &amp; create rule
          </Button>
          <Button variant="outline" onClick={onReject}>
            Reject…
          </Button>
        </>
      );
  }
}

function RejectionOrPauseBanner({
  status,
  updatedAt,
}: {
  status: PatternStatus;
  updatedAt: string | null;
}) {
  // updated_at is the timestamp of the last state transition for the pattern,
  // so we use it as the rejection/pause date. Reason and "by" aren't persisted
  // by the backend yet — see PR description for the follow-up issue.
  if (!updatedAt) return null;
  const dateStr = new Date(updatedAt).toISOString().slice(0, 10);
  const verb = status === "rejected" ? "Rejected" : "Paused";
  return (
    <div className="mt-3 text-xs rounded border bg-muted/40 px-3 py-2">
      <span className="font-medium">
        {verb} on {dateStr}
      </span>
    </div>
  );
}

function StatRow({
  label,
  sparkline,
  value,
}: {
  label: string;
  sparkline: React.ReactNode;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs uppercase tracking-wide text-muted-foreground w-16 shrink-0">
        {label}
      </span>
      <div className="text-muted-foreground">{sparkline}</div>
      <span className="text-sm font-mono ml-auto">{value}</span>
    </div>
  );
}

function Sparkline({
  values,
  ariaLabel,
}: {
  values: number[];
  ariaLabel: string;
}) {
  if (values.length < 2) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  const w = 96;
  const h = 18;
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const span = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - ((v - min) / span) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-label={ariaLabel}
      role="img"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        points={points}
      />
    </svg>
  );
}

// ---------------------------------------------------------------------
// Reject + Pause dialogs
// ---------------------------------------------------------------------

function RejectDialog({
  dialog,
  onCancel,
  onSubmit,
}: {
  dialog:
    | { kind: "single"; item: PatternCurationItem }
    | { kind: "bulk"; items: PatternCurationItem[] };
  onCancel: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const isBulk = dialog.kind === "bulk";
  const targets = isBulk ? dialog.items : [dialog.item];

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isBulk
              ? `Reject ${targets.length} patterns?`
              : `Reject "${targets[0].name}"?`}
          </DialogTitle>
          <DialogDescription>
            Future discovery runs will continue to detect{" "}
            {isBulk ? "these patterns" : "this pattern"} but won't recreate the
            forecast bucket rule.
          </DialogDescription>
        </DialogHeader>
        {isBulk && (
          <ul className="text-xs text-muted-foreground list-disc list-inside max-h-32 overflow-y-auto">
            {targets.map((t) => (
              <li key={t.id}>
                {t.name}{" "}
                <span className="text-muted-foreground/70">
                  · {t.customer_name}
                </span>
              </li>
            ))}
          </ul>
        )}
        <label className="block">
          <span className="text-sm">Reason (optional)</span>
          <textarea
            className="w-full text-sm border rounded p-2 h-20 mt-1 bg-background"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. one-off campaign, not a real recurring stream"
          />
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setBusy(true);
              try {
                await onSubmit(reason);
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
          >
            {busy
              ? "Rejecting…"
              : isBulk
                ? `Reject ${targets.length}`
                : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PauseDialog({
  item,
  onCancel,
  onSubmit,
}: {
  item: PatternCurationItem;
  onCancel: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pause &ldquo;{item.name}&rdquo;?</DialogTitle>
          <DialogDescription>
            Temporarily stop forecasting this pattern. Re-confirm to resume.
          </DialogDescription>
        </DialogHeader>
        <label className="block">
          <span className="text-sm">Reason (optional)</span>
          <textarea
            className="w-full text-sm border rounded p-2 h-20 mt-1 bg-background"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. customer paused trade-show programme for 2026"
          />
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setBusy(true);
              try {
                await onSubmit(reason);
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
          >
            {busy ? "Pausing…" : "Pause"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
